/* eslint-disable no-fallthrough */
// Import core VUE framework.
import Vue from 'vue'

// import { logrocket } from '../logrocket'
import applogger from '@applogger'

// Include the  VUE router toolkit.
import VueRouter from 'vue-router'

import VueObserveVisibility from 'vue-observe-visibility'

// Import custom plugins
import headManagement from './plugins/head-management'

import mainApp from './main-app.vue'

// Import default async loading and error components.
import loadingComponent from './loaders/default-loading'
import errorComponent from './loaders/default-error'

// Import the generic catch all component.
import catchAll from './loaders/catch-all'

import { defaultDynamicRoutes } from './loaders/default-dynamic-routes'
// import io from 'socket.io-client'

// import modulehooks from './module-hooks'

import DrupalStore from 'dev/vuejs-drupal-store'
// import IchabodCMSStore from 'vuejs-ichabodcms-store'

import CallBackRegistry from './callback-registry'
import EventHub from './event-hub'
import createProfiler from './profiler'

import Meta from 'vue-meta'

import SEOSettings from 'dev/seo'
// import { debug } from 'logrocket'
// import { NotFoundError, AuthorizationRequiredError, AccessDeniedError } from 'app/errors'

const inBrowser = () => typeof window !== 'undefined'

// Vue.mixin(pageTitle)

/**
 * Main App class.
 */
class App {
  /**
   * Create the App instance.
   */
  constructor(loader = loadingComponent, error = errorComponent) {
    this.loader = loader
    this.error = error

    this.RavenDSN = false

    // Default values.
    this.router = null
    this.middleware_address = null
    this.ecommerceModule = null
    this.ecommerceOptions = {}
    this.fileStoreSettings = false
    this.routes = []
    this.startupComponents = {}
    this.components = {}
    this.localStorage = false
    this.server = false
    this.registry = {}
    this.preloadStoreItems = []
    this.prefetchStoreItems = []
    this.plugins = []
    this.headMangementOptions = {}
  }

  /**
   * Add the routes for this App instance.
   */
  installRoutes(dynamicRoutes) {
    if (!this.router) {
      let routes = [
        ...dynamicRoutes,
        ...this.routes
      ]
      for (let i = 0; i < routes.length; i++) {
        if (typeof routes[i]['component'] === 'string') {
          if (Object.prototype.hasOwnProperty.call(this.startupComponents, routes[i]['component'])) {
            routes[i]['component'] = this.startupComponents[routes[i]['component']]
          }
          else {
            routes[i]['component'] = this.components[routes[i]['component']]
          }
        }
      }

      routes.push({
        path: '*',
        name: 'Default Catch All',
        component: catchAll,
        meta: {
          'ContentItemCheck': true
        }
      })
      this.router = new VueRouter({
        routes: routes,
        mode: 'history',
        scrollBehavior(to, from, savedPosition) {
          if (savedPosition && (savedPosition.x !== 0 || savedPosition.y !== 0)) return savedPosition
          if (to.hash) return { selector: to.hash, behavior: 'smooth' }
          return { x: 0, y: 0 }
        }
      })
    }
  }

  enableSeo() {
    this.addPlugin(SEOSettings, {})
    this.headMangementOptions = SEOSettings.callbacks
  }

  addPlugin(plugin, options) {
    // run() re-invokes the registry callback (and so addPlugin) on every
    // request; without this guard the same plugin would be pushed again
    // each time and installPlugins() below would re-run its install() once
    // per accumulated duplicate, on every request, forever.
    if (this.plugins.some(item => item.plugin === plugin)) {
      return
    }
    this.plugins.push({ plugin: plugin, options: (options === undefined) ? {} : options })
  }

  /**
   * Enable plugins needed for this App instance.
   */
  installPlugins(options, profiler = createProfiler(false)) {
    this.headMangementOptions.defaultTitle = this.registry.getSiteSetting('title')

    Vue.use(VueRouter)
    Vue.use(Meta)
    Vue.use(headManagement, this.headMangementOptions)
    Vue.use(VueObserveVisibility)

    // Install directly rather than via Vue.use(). Vue.use() records
    // installed plugins on the Vue constructor itself, which is shared
    // across every request whenever the SSR bundle context is reused
    // (e.g. runInNewContext: false/'once'). These plugins are handed this
    // request's freshly-created `store` in `options`, so they must run on
    // every request - with Vue.use(), only the very first request's store
    // ever got wired up (e.g. the seo plugin's Vuex module registration),
    // and every request after it silently ran without it.
    this.plugins.forEach((item, index) => {
      let pluginOptions = {}
      if (typeof item.options === 'object') {
        pluginOptions = { ...options, ...item.options }
      }
      // Plugins don't have a natural unique name the way Vuex actions do
      // (most export a function literally called `install`), so profiling
      // relies on addPlugin(plugin, { name: '...', ... }) being set by the
      // caller - falls back to array index when it isn't.
      const label = `install:${(item.options && item.options.name) || index}`
      profiler.start(label)
      if (typeof item.plugin.install === 'function') {
        // Some plugins (e.g. @aspedia/vuejs-ecommerce-ui) implement their
        // own install-once guard (`if (install.installed) return`) as a
        // defensive idiom against double-installation via Vue.use(). We
        // deliberately bypass Vue.use() and call install() directly every
        // request (see comment above) - that guard silently no-ops every
        // request after the first, because `install.installed` persists on
        // the function object across requests in the SSR bundle's reused
        // module cache (runInNewContext: 'once'). For ecommerce-ui this
        // meant options.store.registerModule('ecommerce', ...) never ran
        // again after request #1, so 'ecommerce/init' was an unknown
        // action on every store after that - store.dispatch() returned
        // undefined instead of a promise, throwing on the caller's .then().
        // Clear the guard before every call so install() actually re-runs
        // against this request's fresh store.
        item.plugin.install.installed = false
        item.plugin.install(Vue, pluginOptions)
      }
      else if (typeof item.plugin === 'function') {
        item.plugin(Vue, pluginOptions)
      }
      profiler.end(label)
      if (typeof item.options.callback === 'function') {
        item.options.callback({ ...options, ...item.options, callback: undefined })
      }
    })
  }

  enableVueJSMiddleWare(status) {
    this.middlewareStatus = status
  }

  setupVueJSMiddleWare() {
    // TODO: perform a ping request to check that the backend is available.
    this.middleware_address = this.location.origin
    if (this.middlewareStatus) {
      let matches = this.location.host.match(/(crm|www|portal|directory|conference)\.(.*)/)
      if (matches) {
        this.middleware_address = this.location.scheme + '//proxy.' + matches[2]
      }
    }
  }

  enableECommerce(module, options) {
    this.ecommerceModule = module
    this.ecommerceOptions = options || {}
  }

  /**
   * Add an individual route.
   * @param route
   */
  addRoute(route) {
    this.routes.push(route)
  }

  /**
   * Add an array of route objects.
   * @param routes
   */
  addRoutes(routes) {
    this.routes = [...this.routes, ...routes]
  }

  /**
   * Add a startup component.
   *
   * @param name
   * @param component
   */
  addStartup(name, component) {
    this.startupComponents[name] = component
  }

  /**
   * Add a lazy loaded component.
   *
   * @param name
   * @param source
   */
  addComponent(name, source) {
    const debugComponentLoading = process.env.DEBUG_COMPONENT_LOADING
    let loader = source
    if (debugComponentLoading) {
      loader = function(name) {
        return new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            console.error(`Component: ${name} .. loading timeout`)
            reject(new Error(`Could Not load compoent: ${name}`))
          }, 500)
          source()
            .then(c => {
              clearTimeout(timer)
              resolve(c)
            })
        })
      }
    }

    this.components[name] = () => ({
      component: loader()
        .then(component => {
          if (typeof component.default.asyncData === 'function') {
            try {
              return component.default.asyncData({ store: this.store })
                .then(_ => {
                  return component
                })
            }
            catch (err) {
              return component
            }
          }
          else {
            return component
          }
        }),
      loading: (process.env.SERVER_BUILD === 'ssr') ? null : this.loader,
      error: this.error,
      delay: (process.env.SERVER_BUILD === 'ssr') ? 0 : 250,
      timeout: (process.env.SERVER_BUILD === 'ssr') ? (debugComponentLoading) ? 5000 : 500 : 30000
    })
  }

  getSocketIORoomName() {
    let socketIORoomName = this.location.host
    let matches = socketIORoomName.match(/(crm|www|portal|directory|conference)\.(.*)/)
    if (matches) {
      socketIORoomName = matches[2]
    }
    return socketIORoomName
  }

  initSocketIO() {
    if (this.middlewareStatus && !this.server) {
      // let socketIOChannel = this.getSocketIORoomName()
      // applogger.debug('Initializing WebSocket to server Middleware', socketIOChannel)
      // let socketListener = io(this.middleware_address,
      //   {
      //     path: '/ws',
      //     autoConnect: true,
      //     reconnection: true,
      //     reconnctionDelay: 1000,
      //     query: 'sitename=' + this.getSocketIORoomName()
      //   }
      // )
      // socketListener.on('connect', () => {
      //   applogger.debug('Connection to server successful: ', socketListener.id)
      // })
      // socketListener.on('cache', (data) => {
      //   Vue.expireItem(data.path)
      // })
      // socketListener.on('route', (data) => {
      //   let route = [
      //     {
      //       path: '/' + data.routeInfo.path,
      //       component: this.components['page-content-type-' + data.routeInfo.type],
      //       props: {
      //         nid: parseInt(data.routeInfo.nid),
      //         name: data.routeInfo.title
      //       },
      //       meta: {
      //         requiresAuth: data.routeInfo.requiresAuth
      //       }
      //     }
      //   ]
      //   try {
      //     this.VueApp.$router.addRoutes(route)
      //     let pa = JSON.parse(localStorage.getItem('/vuejs/path/aliases'))
      //     pa.data[data.routeInfo.type][data.routeInfo.path] = data.routeInfo
      //     pa.timestamp = data.timestamp
      //     localStorage.setItem('/vuejs/path/aliases', JSON.stringify(pa))
      //     applogger.debug('Updated route for path: /' + data.routeInfo.path)
      //   }
      //   catch (e) {
      //     console.error('Error updating locally cached Additional Routes.')
      //     throw e
      //   }
      // })
    }
  }

  initRouterGuard() {
    this.router.beforeEach(async(to, from, next) => {
      applogger.debug('navigating to', to.fullPath)
      this.store.commit('DELETE_PAGE_SETTING', 'error')
      // handle normal routes
      let options = {}
      let requiresAuth = to.meta.requiresAuth ? to.meta.requiresAuth : 'cache'
      if (typeof to.meta.onload === 'function') {
        await to.meta.onload({ store: this.store })
      }

      if (typeof to.meta.beforeEachCheck === 'function') {
        await to.meta.beforeEachCheck(to, this.store, next)
      }

      switch (requiresAuth) {
      case 'allow':
        next()
        return
      case 'always':
        options.noCache = true
      case 'cache':
      default:
        options.meta = to.meta
        if (to.meta.ContentItemCheck !== undefined && to.meta.ContentItemCheck) {
          this.store.dispatch('showProgressLoader')
            .then(_ => this.store.dispatch('node/lookupItem', { path: to.path.toString() }))
            .then(response => {
              if (response) {
                this.store.commit('DELETE_PAGE_SETTING', 'error')
                next()
              }
              else {
                next({ name: 'Error404', params: { originalPath: to.fullPath, code: 404, internal: true } })
              }
            })
            .catch(err => {
              if (err && err.name === 'NotFoundError' && err.code === '404') {
                return next({ name: 'Error404', params: { originalPath: to.fullPath, code: 404, internal: true } })
              }
              this.store.dispatch('logRocketException', { exception: err, data: { options, to } })
              next({ name: 'Error500', params: { originalPath: to.fullPath, code: 500, err: err } })
            })
            .finally(_ => this.store.dispatch('hideProgressLoader'))
        }
        else if (to.meta.CheckAccountPermissions !== undefined && to.meta.CheckAccountPermissions) {
          this.store.dispatch('showProgressLoader')
            .then(_ => {
              if (!this.store.getters['user/isLoggedIn']) {
                next({ name: 'Error401', params: { originalPath: to.fullPath, code: 401, internal: true } })
              }
              else {
                let accessError = to.meta.CheckAccountPermissions.some(value => !this.store.getters['user/checkPermissions'](value))
                if (accessError) {
                  next({ name: 'Error403', params: { originalPath: to.fullPath, permissions: to.meta.CheckAccountPermissions, code: 403 } })
                }
                else {
                  next()
                }
              }
            })
            .catch(err => {
              this.store.dispatch('logRocketException', { exception: err, data: { options, to, internal: true } })
              next({ name: 'Error500', params: { originalPath: to.fullPath, code: 500, err: err, internal: true } })
            })
            .finally(_ => this.store.dispatch('hideProgressLoader'))
        }
        else {
          next()
        }
      }
    })
  }

  addRegistryCallback(func) {
    this.regcallback = func
  }

  /**
 * The main App entry point.
 */
  run(registry, server, scheme, host, port, origin, pathname, profile = false) {
    // Threaded through as a plain value, not stored on `this` - see
    // profiler.js for why (this App instance is a singleton shared across
    // concurrent SSR requests).
    const profiler = createProfiler(!!profile)
    this.registry = registry
    // this.RavenDSN = this.registry.getServiceEndpoint('raven')
    // this.localStorage = inBrowser && this.registry.getSiteSetting('local-storage')
    // this.siteID = this.registry.getSiteId()

    this.server = server
    this.location = {
      scheme: scheme,
      host: host,
      port: port,
      origin: origin,
      pathname: pathname
    }

    let AppStore
    if (this.registry.getStoreType() === 'drupal') {
      AppStore = DrupalStore
    }
    // else {
    //   AppStore = IchabodCMSStore
    // }

    // run() re-invokes regcallback() below on every SSR request against a
    // singleton App instance, and regcallback() unconditionally re-adds
    // routes/preload/prefetch items every time it runs. Without resetting
    // these first, they'd grow by another duplicate copy each request
    // forever, and - since installRoutes() only ever builds this.router
    // once (guarded by `if (!this.router)`) - the very first request this
    // process ever handled would permanently pin the router (and its route
    // table) for every request after it, including requests for other
    // sites sharing this SSR context.
    this.router = null
    this.routes = []
    this.preloadStoreItems = []
    this.prefetchStoreItems = []

    // Add the registry callback provider
    const callbackRegistry = CallBackRegistry()
    Vue.prototype.$callbackregistry = callbackRegistry

    this.regcallback()

    // Initialise logging - only enabled with a build options
    // if (process.env.LOGROCKET_ENABLE) {
    //   logrocket.init(this.registry)
    // }

    this.store = AppStore(this.registry)

    // Add the event hub.
    const eventHub = EventHub(this.registry.getModuleSetting('event-listeners'), this.store)
    Vue.prototype.$eventHub = eventHub

    this.store.$eventHub = eventHub

    profiler.start('installPlugins')
    this.installPlugins({ store: this.store, callbackRegistry, eventHub }, profiler)
    profiler.end('installPlugins')

    // Quick access to the apolloClient Factory
    Vue.prototype.$graphQLClient = this.store.getters['getGraphQLClient']
    Vue.prototype.$registry = this.registry

    Object.keys(this.startupComponents).forEach(name => {
      Vue.component(name, this.startupComponents[name])
      if (typeof this.startupComponents[name].preloadOnStartup === 'function') {
        this.addPreloadItem((store) => this.startupComponents[name].preloadOnStartup(store), `preloadOnStartup:${name}`)
      }
    })
    Object.keys(this.components).forEach(name => Vue.component(name, this.components[name]))
    // initialStoreDispatches times the whole Promise.all; timedDispatch adds
    // a per-action breakdown underneath it so a single slow dispatch (e.g.
    // an upstream API call inside config/init) is visible instead of hiding
    // in the aggregate.
    const timedDispatch = (action, payload) => {
      profiler.start(`dispatch:${action}`)
      return this.store.dispatch(action, payload).then(result => {
        profiler.end(`dispatch:${action}`)
        return result
      })
    }
    const buildDynamicRoutesFromBlobResult = (returnedRoutes) => {
      const dynamicRoutes = (returnedRoutes && Object.keys(returnedRoutes).length > 0) ? returnedRoutes : defaultDynamicRoutes
      return dynamicRoutes.map(route => {
        let beforeEachCheck = ''
        if (route.component === 'page-layout-product-catalogue') {
          beforeEachCheck = function(to, store, next) {
            if (!store.getters['catalogues/isActive'](to.meta.catalogueID) && !store.getters['user/checkPermissions']('administer store')) next({ name: 'Error500', params: { originalPath: to.fullPath, code: 500, err: 'This Catalogue is Unpublished.' } })
          }
        }
        return {
          ...route,
          meta: {
            ...route.meta,
            beforeEachCheck: beforeEachCheck
          }
        }
      })
    }

    // Returns the array of already-firing preload promises (each one
    // starts as soon as this runs, not lazily) - callers decide how/when
    // to wait on them.
    const startPreloads = () => {
      applogger.log('Loading App Preloaded Items')
      profiler.start('preloadStoreItems')
      return this.preloadStoreItems.map((func, index) => {
        const label = `preload:${func.$profileName || index}`
        profiler.start(label)
        const result = func.call(this, { callbackRegistry: callbackRegistry, store: this.store, profiler })
        return Promise.resolve(result).then(r => {
          profiler.end(label)
          return r
        })
      })
    }

    const finalizeApp = () => {
      this.prefetchStoreItems.map(func => func.call(this, { store: this.store }))
      applogger.debug('Creating App Instance')
      this.store.dispatch('logRocketIdentify')
      this.VueApp = new Vue({
        router: this.router,
        store: this.store,
        render: h => h(mainApp, { props: { classes: this.registry.getModuleSetting('theme', 'page-classes', '') } })
      })

      if (inBrowser() && window.Cypress) {
        console.log('App loaded for Cypress testing')
        window.__app__ = this.VueApp
      }

      this.plugins.map(plugin => {
        if (typeof plugin.onAppStart === 'function') {
          plugin.onAppStart({ app: this.VueApp, store: this.store, router: this.router })
        }
      })

      if (profiler.enabled) {
        // applogger is a no-op in production builds (DefinePlugin bakes
        // NODE_ENV in at build time and Terser strips the console.*
        // bindings entirely - see vuejs-src/applogger.js). Profiling is
        // only useful if its output actually reaches the SSR host's
        // logs, so this deliberately calls console.log directly rather
        // than going through applogger like the rest of this file.
        console.log('ssr-profile:run', { phases: profiler.summary() })
      }

      return {
        vueapp: this.VueApp,
        router: this.router,
        store: this.store,
        vue: Vue,
        profiler
      }
    }

    if (server) {
      // SSR only. The 6 non-routing dispatches, the dynamic-routes
      // sub-chain, and every registered preload item are structurally
      // independent of one another (verified: none of them read config/
      // node.commonItems/filestore/user state before dispatching - see
      // addPreloadItem's comment below), so all of them fire immediately
      // and run concurrently instead of gating preloads behind the whole
      // 7-dispatch batch resolving first, the way the client build below
      // still does. This removes ~200ms of pure sequential wait measured
      // on a warm SSR render (was ~206ms + ~307ms sequential, now bound
      // by the slowest single item, ~307ms).
      profiler.start('ssrConcurrentBootstrap')

      profiler.start('initialStoreDispatches')
      const otherDispatches = Promise.all([
        timedDispatch('config/init'),
        timedDispatch('node/loadCommonItemsLookup'),
        timedDispatch('user/getAnonymousPermissions'),
        timedDispatch('user/init'),
        timedDispatch('filestore/init'),
        timedDispatch('setLogRocket', {
          logRocket: this.$logRocket,
          logRocketId: this.registry.getSiteSetting('logrocketId'),
          enabled: this.registry.getSiteSetting('logrocketEnabled')
        })
      ]).then(result => {
        profiler.end('initialStoreDispatches')
        return result
      })

      // The one real structural dependency: blob/load -> installRoutes ->
      // loadAsyncData. Starts immediately and runs concurrently with
      // everything else, but its own three steps stay sequential - routes
      // must be installed before the router can match the current URL for
      // asyncData resolution.
      profiler.start('dynamicRoutesChain')
      const dynamicRoutesChain = timedDispatch('blob/load', { blobID: 'dynamic-routes-list', storeBlob: false })
        .then(returnedRoutes => {
          this.installRoutes(buildDynamicRoutesFromBlobResult(returnedRoutes))
          return this.loadAsyncData([mainApp], undefined, profiler)
        })
        .then(result => {
          profiler.end('dynamicRoutesChain')
          return result
        })

      const preloadsSettled = Promise.all(startPreloads()).then(result => {
        profiler.end('preloadStoreItems')
        return result
      })

      return Promise.all([otherDispatches, dynamicRoutesChain, preloadsSettled])
        .then(() => {
          profiler.end('ssrConcurrentBootstrap')
          return finalizeApp()
        })
    }

    // Client build: kept as the existing sequential two-phase structure -
    // config/init and filestore/init read user/verifiedJWT (via
    // AuthorizedRequestConfig), which IS real client-side (populated from
    // localStorage), so racing them ahead of user/init here would be a
    // genuine behaviour change for logged-in users, unlike the SSR path
    // above where that state is architecturally always empty.
    profiler.start('initialStoreDispatches')
    return Promise.all([
      timedDispatch('blob/load', { blobID: 'dynamic-routes-list', storeBlob: false }),
      timedDispatch('config/init'),
      timedDispatch('node/loadCommonItemsLookup'),
      timedDispatch('user/getAnonymousPermissions'),
      timedDispatch('user/init'),
      timedDispatch('filestore/init'),
      timedDispatch('setLogRocket', {
        logRocket: this.$logRocket,
        logRocketId: this.registry.getSiteSetting('logrocketId'),
        enabled: this.registry.getSiteSetting('logrocketEnabled')
      })
    ])
      .then(([returnedRoutes]) => {
        profiler.end('initialStoreDispatches')
        this.installRoutes(buildDynamicRoutesFromBlobResult(returnedRoutes))
        this.initRouterGuard()
        this.initSocketIO()
        const preloads = startPreloads()
        preloads.push(this.loadAsyncData([mainApp], undefined, profiler))
        return Promise.all(preloads).then(result => {
          profiler.end('preloadStoreItems')
          return result
        })
      })
      .then(finalizeApp)
  }

  /**
 * Prefetch Items are loaded asynchronously with the application start and
 * should contain content to be loaded into the VueX Store that us used across the
 * site.
 *
 * The
 * @param {*} item - a single or array of promise based functions that will load the required items
 */
  addPrefetchItem(item) {
    if (item instanceof Array) {
      this.prefetchStoreItems = [...this.prefetchStoreItems, ...item]
    }
    else {
      this.prefetchStoreItems = [...this.prefetchStoreItems, item]
    }
  }

  /**
 * Preload Items are loaded prior to the appliction being mounted. You should only preload Items
 * that are absolutely required for the page load. This is for items such as the primary menu or
 * page footer content. Adding many items to the list will substantially slow dont the inital
 * load of the page.
 *
 * The
 * @param {*} item - a single or array of promise based functions that will load the required items
 * @param {string} [name] - optional profiling label; tagged onto the function(s) themselves
 *   (as $profileName) rather than restructuring preloadStoreItems into {fn, name} pairs, so
 *   run()'s existing "array of callables" handling doesn't need to change. Falls back to array
 *   index in the profiler output when omitted.
 *
 * On SSR, run() fires preload items concurrently with config/init, node/
 * loadCommonItemsLookup, user/getAnonymousPermissions, user/init, and
 * filestore/init (not after them) - do not assume any of those dispatches
 * have resolved when a preload item runs. This is safe for the currently
 * registered preloads (none of them read that state before dispatching),
 * but is a standing invariant new preloads must respect. The client build
 * keeps the old sequential ordering, where those dispatches do resolve
 * first.
 */
  addPreloadItem(storeItems, name) {
    const items = storeItems instanceof Array ? storeItems : [storeItems]
    if (name) {
      items.forEach((fn, i) => {
        fn.$profileName = items.length > 1 ? `${name}:${i}` : name
      })
    }
    this.preloadStoreItems = [...this.preloadStoreItems, ...items]
  }

  loadAsyncData(matchedComponents, route, profiler = createProfiler(false)) {
    applogger.debug('Load Async Data...')
    applogger.debug('Vue SSR Context: ' + this.$ssrRunMode)

    let keyCache = []
    let appStore = this.store
    if (route === undefined) {
      route = this.router.currentRoute
    }
    const doAsyncData = (component) => {
      if (component.asyncData) {
        return component.asyncData.call(this, { route: route, store: appStore, router: this.router, app: this })
      }
      else if (component.extendOptions && component.extendOptions.asyncData) {
        return component.extendOptions.asyncData({ route: route, router: this.router, store: appStore, app: this })
      }
      else {
        return Promise.resolve(true)
      }
    }

    // Some component names resolve to a factory that doesn't return a Promise
    // (e.g. a component registered via a plain `Vue.component(name, options)`
    // rather than the app's own lazy-loading `addComponent`). Calling those
    // factories and chaining `.then()` straight onto the result throws
    // synchronously. Wrap in a helper so any such mismatch becomes a normal,
    // catchable promise rejection instead of an unhandled crash.
    const toPromise = (fn) => {
      try {
        const result = fn()
        return (result && typeof result.then === 'function') ? result : Promise.resolve(result)
      }
      catch (err) {
        return Promise.reject(err)
      }
    }

    const recursive = (component, key) => {
      let promises = []
      if (keyCache.indexOf(key) === -1) {
        keyCache.push(key)
        profiler.start(`asyncData:${key}`)
        promises.push(
          Promise.resolve(doAsyncData(component)).then(result => {
            profiler.end(`asyncData:${key}`)
            return result
          })
        )
        if (component.asyncComponents) {
          let ASYNCComponents = []
          if (typeof component.asyncComponents === 'function') {
            ASYNCComponents = component.asyncComponents(this)
          }
          else if (component.asyncComponents.constructor === Array) {
            ASYNCComponents = component.asyncComponents
          }
          ASYNCComponents.map(name => {
            applogger.log('Processing component: ', name)
            if (Object.prototype.hasOwnProperty.call(this.startupComponents, name)) {
              promises.push(recursive(this.startupComponents[name], name))
            }
            else if (this.components[name] !== undefined) {
              promises.push(toPromise(() => this.components[name].call().component)
                .then(c => recursive(c.default, c.default.name))
              )
            }
            else {
              if (typeof Vue.options.components[name] === 'function') {
                promises.push(toPromise(() => Vue.options.components[name].call())
                  .then(c => recursive(c.default, c.default.name))
                )
              }
              else if (typeof Vue.options.components[name] !== 'undefined') {
                let c = Vue.options.components[name]
                promises.push(Promise.resolve(recursive(c, c.name)))
              }
            }
          })
        }
        else if (component.components) {
          Object.keys(component.components).forEach(key => {
            promises.push(recursive(component.components[key], key))
          })
        }
      }
      return Promise.all(promises)
    }

    // call `asyncData()` on all matched route components
    return Promise.resolve().then(() => {
      appStore.commit('SET_PAGE_SETTING', { key: 'hasHeader', value: false })
    }).then(() => {
      return Promise.all(matchedComponents
        .filter(x => x)
        .map(component => recursive(component, component.name)))
    })
  }
}

Vue.directive('plaintext', {
  bind(el, binding, vnode) {
    el.innerHTML = el.innerText
  }
})

export {
  App
}
